import { FormattedMessage } from "react-intl";
import { Box, Button, Grid, Icon, Text } from "@chakra-ui/react";
import { AiFillBank } from "react-icons/ai";

import { blockedCTAProps, useTOSGatedAction } from "components/JustInTimeTOS";
import { useGenerateUrlAndRedirectUser } from "./useGenerateUrlAndRedirectUser";

/**
 * @param {Object} props
 * @param {boolean} [props.newUI]
 * @param {string} [props.description]
 * @param {boolean} [props.requireTOS=false] - opt in to just-in-time ToS gating. The gate lives on
 * the button rather than in useGenerateUrlAndRedirectUser because AddPaymentMethodContent calls
 * that hook too, and `requireTOS` belongs on a component.
 */
const AddPlaidACHButton = ({ newUI, description, requireTOS = false }) => {
  const { onGenerateUrlAndRedirectUser, isOpen } = useGenerateUrlAndRedirectUser();
  const { run, blocked: blockedByTOS } = useTOSGatedAction({
    enabled: requireTOS,
    flowLoc: "PLAID.SIGN_TOS",
  });

  /**
   * This departure leaves the widget, so it is a CTA: sign before the redirect is prepared. Both
   * onClick sites go through here rather than handing `onGenerateUrlAndRedirectUser` to React bare,
   * since `run` must own the click (see useTOSGatedAction). The default variant's isDisabled keys
   * off the same isOpen the hook's spinner does, plus `blocked` - matching AddPaypalButton, which
   * sits directly beneath this one on the Rebilly form, so the two departures have to read the same
   * when consent is outstanding. Presentation only either way: `run` is what enforces the gate.
   * The newUI Box has no disabled concept, so it reads as blocked through blockedCTAProps instead.
   */
  const handleClick = () => run(onGenerateUrlAndRedirectUser);

  if (newUI) {
    return (
      <Box
        data-testid="add-bank-button-new-ui"
        p="4"
        boxShadow="base"
        cursor="pointer"
        {...blockedCTAProps(blockedByTOS)}
        onClick={handleClick}
      >
        <Grid templateColumns="20px 1fr" gridGap="2" alignItems="center">
          <Icon as={AiFillBank} boxSize="5" color="integrator.500" />
          <Text fontWeight="bold">
            <FormattedMessage defaultMessage="Bank" description="Button label" />
          </Text>
          <Box />
          <Box>{description}</Box>
        </Grid>
      </Box>
    );
  }

  return (
    <Button
      data-testid="add-bank-button"
      variant="outline"
      colorScheme="gray"
      onClick={handleClick}
      width="full"
      isDisabled={isOpen || blockedByTOS}
    >
      <FormattedMessage defaultMessage="Add Bank Account" description="Button label" />
    </Button>
  );
};

export default AddPlaidACHButton;
